/**
 * Access Control Type Definitions
 *
 * Strongly-typed constants for access matrix resources and actions.
 * Use these instead of magic strings throughout the application.
 *
 * RBAC Model:
 * - Role flags (is_rc_hrbp, is_rc_executive, is_rc_manager, is_rc_employee) = DATA SCOPE (who can see)
 * - permissions JSON = FEATURE ACCESS (what can do)
 * - NO hardcoded bypasses - all access from permissions
 */

// ============================================
// Module Identifiers
// ============================================

/**
 * Top-level modules in the application
 */
export const Module = {
  CORE: 'core',         // Includes workforce entities (workforce is a submodule of core)
  PERFORM_SPARK: 'performspark',
  LEARNING: 'learn',
  COMPENSATION: 'compensation',
} as const;

export type Module = (typeof Module)[keyof typeof Module];

// ============================================
// Resource Identifiers
// ============================================

/**
 * All resources in PerformSpark module
 * These match the keys in your JWT access_matrix (permissions JSON)
 */
export const PerformSparkResource = {
  // Goal Management
  GOAL_CYCLES: 'performspark.goal_management.goal_cycles',
  GOAL_TEMPLATES: 'performspark.goal_management.goal_templates',

  // Performance Management
  PERFORMANCE_CYCLES: 'performspark.performance_management.cycles',
  PERFORMANCE_TEMPLATE: 'performspark.performance_management.template',

  // Talent Management
  TALENT_CYCLES: 'performspark.talent_management.cycles',
  TALENT_IDP: 'performspark.talent_management.idp',
  TALENT_TEMPLATES: 'performspark.talent_management.templates',
  TALENT_POOLS: 'performspark.talent_management.talent_pools',
  TALENT_CRITICAL_ROLES: 'performspark.talent_management.critical_roles',

  // Survey Management
  SURVEY_TEMPLATES: 'performspark.survey_management.templates',
  SURVEY_QUESTIONS: 'performspark.survey_management.questions',
  SURVEY_CAMPAIGNS: 'performspark.survey_management.campaigns',
  SURVEY_RESPONSES: 'performspark.survey_management.responses',
} as const;

export type PerformSparkResource = (typeof PerformSparkResource)[keyof typeof PerformSparkResource];

/**
 * All resources in Learn LMS module
 * These match the keys in your JWT access_matrix
 */
export const LearnResource = {
  // LMS - Course Management
  COURSES: 'learn.lms.courses',
  LEARNERS: 'learn.lms.learners',
  LICENSES: 'learn.lms.licenses',
  ANALYTICS: 'learn.lms.analytics',

  // LMS - View Pool Management
  VIEW_POOLS: 'learn.lms.view_pools',

  // LMS - Assignment & Enrollment
  ASSIGNMENTS: 'learn.lms.assignments',
  ENROLLMENTS: 'learn.lms.enrollments',

  // LMS - Customer Assignments
  CUSTOMER_ASSIGNMENTS: 'learn.lms.customer_assignments',

  // LMS - Reporting
  REPORTS: 'learn.lms.reports',
  DASHBOARD: 'learn.lms.dashboard',

  // LMS - Course Creator
  COURSE_CREATOR: 'learn.lms.course_creator',

  // TMS - Training Management System
  TMS_EVENTS: 'learn.tms.events',
  TMS_TRAINERS: 'learn.tms.trainers',

  // Coaching
  COACHING_COACHES: 'learn.coaching.coaches',
  COACHING_COACHEES: 'learn.coaching.coachees',
  COACHING_ENGAGEMENTS: 'learn.coaching.engagements',
  COACHING_SESSIONS: 'learn.coaching.sessions',
  COACHING_GOALS: 'learn.coaching.goals',
  COACHING_COACH_CORNER: 'learn.coaching.coach_corner',
  COACHING_PROGRESS: 'learn.coaching.progress',
  COACHING_LIBRARY: 'learn.coaching.library',
} as const;

export type LearnResource = (typeof LearnResource)[keyof typeof LearnResource];

/**
 * Resources in the Core module (org administration, access management)
 */
export const CoreResource = {
  ACCESS_MANAGEMENT: 'core.organization.access_management',
  NOTIFICATIONS: 'core.organization.notifications',
  DOCUSTOR: 'core.organization.docustor',
} as const;

export type CoreResource = (typeof CoreResource)[keyof typeof CoreResource];

/**
 * Resources in the Workforce module
 */
export const WorkforceResource = {
  EMPLOYEES: 'workforce.employees',
} as const;

export type WorkforceResource = (typeof WorkforceResource)[keyof typeof WorkforceResource];

/**
 * @deprecated Use PerformSparkResource instead
 * Kept for backward compatibility during migration
 */
export const ReviewCloudResource = PerformSparkResource;

/**
 * Simplified resource identifiers for UI-friendly names
 * These map to full resource paths internally
 */
export const SimpleResource = {
  GOAL_MANAGEMENT: 'goal-management',
  PERFORMANCE_MANAGEMENT: 'performance-management',
  TALENT_MANAGEMENT: 'talent-management',
  SURVEY_MANAGEMENT: 'survey-management',
} as const;

export type SimpleResource = (typeof SimpleResource)[keyof typeof SimpleResource];

// ============================================
// Action Types
// ============================================

/**
 * Available actions for resources
 * These match the actions in your JWT access_matrix
 */
export const Action = {
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  VIEW: 'view',
} as const;

export type Action = (typeof Action)[keyof typeof Action];

// ============================================
// Type-Safe Access Constants
// ============================================

/**
 * Combined resource and action for common operations
 * Use these for type-safe access checks
 *
 * @example
 * if (hasAccess('GOAL_CYCLES_CREATE')) {
 *   // Show create button
 * }
 */
export const Access = {
  // Goal Management - Goal Cycles
  GOAL_CYCLES_CREATE: { resource: PerformSparkResource.GOAL_CYCLES, action: Action.CREATE },
  GOAL_CYCLES_EDIT: { resource: PerformSparkResource.GOAL_CYCLES, action: Action.EDIT },
  GOAL_CYCLES_DELETE: { resource: PerformSparkResource.GOAL_CYCLES, action: Action.DELETE },
  GOAL_CYCLES_VIEW: { resource: PerformSparkResource.GOAL_CYCLES, action: Action.VIEW },

  // Goal Management - Goal Templates
  GOAL_TEMPLATES_CREATE: { resource: PerformSparkResource.GOAL_TEMPLATES, action: Action.CREATE },
  GOAL_TEMPLATES_EDIT: { resource: PerformSparkResource.GOAL_TEMPLATES, action: Action.EDIT },
  GOAL_TEMPLATES_DELETE: { resource: PerformSparkResource.GOAL_TEMPLATES, action: Action.DELETE },
  GOAL_TEMPLATES_VIEW: { resource: PerformSparkResource.GOAL_TEMPLATES, action: Action.VIEW },

  // Performance Management - Cycles
  PERFORMANCE_CYCLES_CREATE: { resource: PerformSparkResource.PERFORMANCE_CYCLES, action: Action.CREATE },
  PERFORMANCE_CYCLES_EDIT: { resource: PerformSparkResource.PERFORMANCE_CYCLES, action: Action.EDIT },
  PERFORMANCE_CYCLES_DELETE: { resource: PerformSparkResource.PERFORMANCE_CYCLES, action: Action.DELETE },
  PERFORMANCE_CYCLES_VIEW: { resource: PerformSparkResource.PERFORMANCE_CYCLES, action: Action.VIEW },

  // Performance Management - Template
  PERFORMANCE_TEMPLATE_CREATE: { resource: PerformSparkResource.PERFORMANCE_TEMPLATE, action: Action.CREATE },
  PERFORMANCE_TEMPLATE_EDIT: { resource: PerformSparkResource.PERFORMANCE_TEMPLATE, action: Action.EDIT },
  PERFORMANCE_TEMPLATE_DELETE: { resource: PerformSparkResource.PERFORMANCE_TEMPLATE, action: Action.DELETE },
  PERFORMANCE_TEMPLATE_VIEW: { resource: PerformSparkResource.PERFORMANCE_TEMPLATE, action: Action.VIEW },

  // Talent Management - Cycles
  TALENT_CYCLES_CREATE: { resource: PerformSparkResource.TALENT_CYCLES, action: Action.CREATE },
  TALENT_CYCLES_EDIT: { resource: PerformSparkResource.TALENT_CYCLES, action: Action.EDIT },
  TALENT_CYCLES_DELETE: { resource: PerformSparkResource.TALENT_CYCLES, action: Action.DELETE },
  TALENT_CYCLES_VIEW: { resource: PerformSparkResource.TALENT_CYCLES, action: Action.VIEW },

  // Talent Management - IDP
  TALENT_IDP_CREATE: { resource: PerformSparkResource.TALENT_IDP, action: Action.CREATE },
  TALENT_IDP_EDIT: { resource: PerformSparkResource.TALENT_IDP, action: Action.EDIT },
  TALENT_IDP_DELETE: { resource: PerformSparkResource.TALENT_IDP, action: Action.DELETE },
  TALENT_IDP_VIEW: { resource: PerformSparkResource.TALENT_IDP, action: Action.VIEW },

  // Talent Management - Templates
  TALENT_TEMPLATES_CREATE: { resource: PerformSparkResource.TALENT_TEMPLATES, action: Action.CREATE },
  TALENT_TEMPLATES_EDIT: { resource: PerformSparkResource.TALENT_TEMPLATES, action: Action.EDIT },
  TALENT_TEMPLATES_DELETE: { resource: PerformSparkResource.TALENT_TEMPLATES, action: Action.DELETE },
  TALENT_TEMPLATES_VIEW: { resource: PerformSparkResource.TALENT_TEMPLATES, action: Action.VIEW },

  // Talent Management - Talent Pools
  TALENT_POOLS_VIEW: { resource: PerformSparkResource.TALENT_POOLS, action: Action.VIEW },
  TALENT_POOLS_CREATE: { resource: PerformSparkResource.TALENT_POOLS, action: Action.CREATE },
  TALENT_POOLS_EDIT: { resource: PerformSparkResource.TALENT_POOLS, action: Action.EDIT },
  TALENT_POOLS_DELETE: { resource: PerformSparkResource.TALENT_POOLS, action: Action.DELETE },

  // Talent Management - Critical Roles
  TALENT_CRITICAL_ROLES_VIEW: { resource: PerformSparkResource.TALENT_CRITICAL_ROLES, action: Action.VIEW },
  TALENT_CRITICAL_ROLES_CREATE: { resource: PerformSparkResource.TALENT_CRITICAL_ROLES, action: Action.CREATE },
  TALENT_CRITICAL_ROLES_EDIT: { resource: PerformSparkResource.TALENT_CRITICAL_ROLES, action: Action.EDIT },
  TALENT_CRITICAL_ROLES_DELETE: { resource: PerformSparkResource.TALENT_CRITICAL_ROLES, action: Action.DELETE },

  // Survey Management
  SURVEY_TEMPLATES_CREATE: { resource: PerformSparkResource.SURVEY_TEMPLATES, action: Action.CREATE },
  SURVEY_TEMPLATES_EDIT: { resource: PerformSparkResource.SURVEY_TEMPLATES, action: Action.EDIT },
  SURVEY_TEMPLATES_DELETE: { resource: PerformSparkResource.SURVEY_TEMPLATES, action: Action.DELETE },
  SURVEY_TEMPLATES_VIEW: { resource: PerformSparkResource.SURVEY_TEMPLATES, action: Action.VIEW },
  SURVEY_QUESTIONS_CREATE: { resource: PerformSparkResource.SURVEY_QUESTIONS, action: Action.CREATE },
  SURVEY_QUESTIONS_EDIT: { resource: PerformSparkResource.SURVEY_QUESTIONS, action: Action.EDIT },
  SURVEY_QUESTIONS_DELETE: { resource: PerformSparkResource.SURVEY_QUESTIONS, action: Action.DELETE },
  SURVEY_QUESTIONS_VIEW: { resource: PerformSparkResource.SURVEY_QUESTIONS, action: Action.VIEW },
  SURVEY_CAMPAIGNS_CREATE: { resource: PerformSparkResource.SURVEY_CAMPAIGNS, action: Action.CREATE },
  SURVEY_CAMPAIGNS_EDIT: { resource: PerformSparkResource.SURVEY_CAMPAIGNS, action: Action.EDIT },
  SURVEY_CAMPAIGNS_DELETE: { resource: PerformSparkResource.SURVEY_CAMPAIGNS, action: Action.DELETE },
  SURVEY_CAMPAIGNS_VIEW: { resource: PerformSparkResource.SURVEY_CAMPAIGNS, action: Action.VIEW },
  SURVEY_RESPONSES_VIEW: { resource: PerformSparkResource.SURVEY_RESPONSES, action: Action.VIEW },

  // ============================================
  // Learn LMS Module
  // ============================================

  // LMS - Courses
  COURSES_CREATE: { resource: LearnResource.COURSES, action: Action.CREATE },
  COURSES_EDIT: { resource: LearnResource.COURSES, action: Action.EDIT },
  COURSES_DELETE: { resource: LearnResource.COURSES, action: Action.DELETE },
  COURSES_VIEW: { resource: LearnResource.COURSES, action: Action.VIEW },

  // LMS - Course Creator
  COURSE_CREATOR_CREATE: { resource: LearnResource.COURSE_CREATOR, action: Action.CREATE },
  COURSE_CREATOR_EDIT: { resource: LearnResource.COURSE_CREATOR, action: Action.EDIT },
  COURSE_CREATOR_DELETE: { resource: LearnResource.COURSE_CREATOR, action: Action.DELETE },
  COURSE_CREATOR_VIEW: { resource: LearnResource.COURSE_CREATOR, action: Action.VIEW },

  // LMS - View Pools
  VIEW_POOLS_CREATE: { resource: LearnResource.VIEW_POOLS, action: Action.CREATE },
  VIEW_POOLS_EDIT: { resource: LearnResource.VIEW_POOLS, action: Action.EDIT },
  VIEW_POOLS_DELETE: { resource: LearnResource.VIEW_POOLS, action: Action.DELETE },
  VIEW_POOLS_VIEW: { resource: LearnResource.VIEW_POOLS, action: Action.VIEW },

  // LMS - Assignments (Course→Learner)
  ASSIGNMENTS_CREATE: { resource: LearnResource.ASSIGNMENTS, action: Action.CREATE },
  ASSIGNMENTS_EDIT: { resource: LearnResource.ASSIGNMENTS, action: Action.EDIT },
  ASSIGNMENTS_DELETE: { resource: LearnResource.ASSIGNMENTS, action: Action.DELETE },
  ASSIGNMENTS_VIEW: { resource: LearnResource.ASSIGNMENTS, action: Action.VIEW },

  // LMS - Customer Assignments (Course→Customer)
  CUSTOMER_ASSIGNMENTS_CREATE: { resource: LearnResource.CUSTOMER_ASSIGNMENTS, action: Action.CREATE },
  CUSTOMER_ASSIGNMENTS_EDIT: { resource: LearnResource.CUSTOMER_ASSIGNMENTS, action: Action.EDIT },
  CUSTOMER_ASSIGNMENTS_DELETE: { resource: LearnResource.CUSTOMER_ASSIGNMENTS, action: Action.DELETE },
  CUSTOMER_ASSIGNMENTS_VIEW: { resource: LearnResource.CUSTOMER_ASSIGNMENTS, action: Action.VIEW },

  // LMS - Learners
  LEARNERS_VIEW: { resource: LearnResource.LEARNERS, action: Action.VIEW },

  // LMS - Dashboard & Analytics
  DASHBOARD_VIEW: { resource: LearnResource.DASHBOARD, action: Action.VIEW },
  ANALYTICS_VIEW: { resource: LearnResource.ANALYTICS, action: Action.VIEW },

  // TMS - Events
  TMS_EVENTS_CREATE: { resource: LearnResource.TMS_EVENTS, action: Action.CREATE },
  TMS_EVENTS_EDIT: { resource: LearnResource.TMS_EVENTS, action: Action.EDIT },
  TMS_EVENTS_DELETE: { resource: LearnResource.TMS_EVENTS, action: Action.DELETE },
  TMS_EVENTS_VIEW: { resource: LearnResource.TMS_EVENTS, action: Action.VIEW },

  // TMS - Trainers
  TMS_TRAINERS_CREATE: { resource: LearnResource.TMS_TRAINERS, action: Action.CREATE },
  TMS_TRAINERS_EDIT: { resource: LearnResource.TMS_TRAINERS, action: Action.EDIT },
  TMS_TRAINERS_VIEW: { resource: LearnResource.TMS_TRAINERS, action: Action.VIEW },

  // Coaching - Engagements
  COACHING_ENGAGEMENTS_CREATE: { resource: LearnResource.COACHING_ENGAGEMENTS, action: Action.CREATE },
  COACHING_ENGAGEMENTS_EDIT: { resource: LearnResource.COACHING_ENGAGEMENTS, action: Action.EDIT },
  COACHING_ENGAGEMENTS_VIEW: { resource: LearnResource.COACHING_ENGAGEMENTS, action: Action.VIEW },

  // Coaching - Coaches
  COACHING_COACHES_VIEW: { resource: LearnResource.COACHING_COACHES, action: Action.VIEW },
  COACHING_COACHES_CREATE: { resource: LearnResource.COACHING_COACHES, action: Action.CREATE },
} as const;

// Type for Access object keys
export type AccessKey = keyof typeof Access;

// ============================================
// Type Helpers
// ============================================

/**
 * Type for access check parameters
 */
export interface AccessCheck {
  resource: PerformSparkResource | SimpleResource | LearnResource;
  action?: Action;
}

/**
 * Type guard to check if a string is a valid Action
 */
export function isValidAction(action: string): action is Action {
  return Object.values(Action).includes(action as Action);
}

/**
 * Type guard to check if a string is a valid PerformSparkResource
 */
export function isValidResource(resource: string): resource is PerformSparkResource {
  return Object.values(PerformSparkResource).includes(resource as PerformSparkResource);
}

/**
 * Type guard to check if a string is a valid SimpleResource
 */
export function isValidSimpleResource(resource: string): resource is SimpleResource {
  return Object.values(SimpleResource).includes(resource as SimpleResource);
}
